import { useAtomValue } from 'jotai';
import { filterAudioLanguageInputState, FacetState } from '@src/pages/Discover/components/Atoms/Atoms';
import { useFiltersParams } from '@src/hooks/useFiltersParams';
import { onFilterChange } from '@src/fragments/Filters/utils/FilterStateUtility';
import { useMediaFindFacet } from '@src/queries/media/useQueryMediaFind';
import { LanguageSearch } from '@src/fragments/Filters/managers/LanguageSearch';
import { SearchSuggestion } from '@src/types/Filters';
import { XclusionUtility } from '@src/fragments/Filters/managers/XclusionUtility';
import { validAudioLanguageCodes } from '@src/fragments/Filters/utils/AudioLanguage';
import { Suggestions } from '../Suggestions';
import { LanguageItem } from '../FilterItem/LanguageItem';

/**
 * Return the audio-language facet values matching the local search input.
 * Facet values are language codes; users can search by either code or name.
 */
export function getAudioLanguageSuggestions(audioLanguages: string[], input: string): SearchSuggestion[] {
  const normalizedInput = input.trim().toLocaleLowerCase();
  return validAudioLanguageCodes(audioLanguages)
    .map((code) => ({
      key: code,
      display: LanguageSearch.translateIso6393(code),
    }))
    .filter(({ key, display }) => !normalizedInput
      || key.toLocaleLowerCase().includes(normalizedInput)
      || display.toLocaleLowerCase().includes(normalizedInput));
}

export function AudioLanguageSuggestion() {
  const { filters, setFilters } = useFiltersParams();
  const input = useAtomValue(filterAudioLanguageInputState);
  const facetState = useAtomValue(FacetState);
  const { data } = useMediaFindFacet(
    facetState.mediaConstraints,
    '',
    'audioLanguages',
    0,
    facetState.enableFacet,
  );

  const audioLanguages = data?.pages?.[0]?.facets?.audioLanguages ?? [];
  // Keep selected values visible even when the facet response no longer
  // contains them (or while the user is searching for another value).
  const validSelectedCodes = new Set(validAudioLanguageCodes(filters.audioLang.map((value) => XclusionUtility.getRawKey(value))));
  const selectedLanguages = filters.audioLang
    .filter((value) => validSelectedCodes.has(XclusionUtility.getRawKey(value)))
    .map((value) => ({
      key: value,
      display: LanguageSearch.translateIso6393(XclusionUtility.getRawKey(value)),
    }));
  const matchingLanguages = getAudioLanguageSuggestions(audioLanguages, input);

  return (
    <Suggestions
      onChange={onFilterChange(setFilters, 'audioLang')}
      selectedValues={renderValues(selectedLanguages)}
      values={renderValues(matchingLanguages)}
    />
  );
}

function renderValues(suggestions: SearchSuggestion[]) {
  return suggestions.map((suggestion) => ({
    key: suggestion.key,
    children: <LanguageItem suggestion={suggestion} />,
  }));
}
